import { useTranslations } from '@vocab/react';
import {
  Badge,
  Box,
  Button,
  Column,
  Columns,
  Heading,
  IconFilter,
  IconSearch,
  Stack,
  Text,
  TextField,
  Hidden,
  ContentBlock,
} from 'braid-design-system';
import type { FormEventHandler } from 'react';

import type { Market } from '../../types';
import { MarketSelector } from '../MarketSelector/MarketSelector';

import translations from './.vocab';
import leftShapesImgUrl from './images/left-shapes.png';
import rightShapesImgUrl from './images/right-shapes.png';
import userImageAU from './images/user-image-AU.png';
import userImageHK from './images/user-image-HK.png';
import userImageMY from './images/user-image-MY.png';
import userImageNZ from './images/user-image-NZ.png';
import userImageSG from './images/user-image-SG.png';
import userImageIdPhTh from './images/user-image-id-ph-th.png';

import {
  leftShapesImgStyle,
  rightShapesImgStyle,
  shapesImageStyle,
  userProfilesImgStyle,
} from './SearchBanner.css';

interface Props {
  searchQuery: {
    value: string;
    onChange: FormEventHandler<HTMLInputElement>;
    onClear: () => void;
    valid: boolean;
    errorMessage?: string;
  };
  market: {
    value: Market;
    onChange: (marketSelected: Market) => void;
  };
  domainMarket: Market;
  filterItemsCount: number;
  onSubmit: FormEventHandler<HTMLFormElement>;
  onFiltersButtonClick: () => void;
}

const getImageFileName = (market: Market): string => {
  switch (market) {
    case 'TH':
    case 'ID':
    case 'PH':
      return userImageIdPhTh;
    case 'HK':
      return userImageHK;
    case 'MY':
      return userImageMY;
    case 'NZ':
      return userImageNZ;
    case 'SG':
      return userImageSG;
    case 'AU':
    default:
      return userImageAU;
  }
};

export const SearchBanner = ({
  searchQuery: {
    value: searchQuery,
    onChange: onSearchQueryChange,
    onClear: onSearchQueryClear,
    valid,
    errorMessage,
  },
  market: { value: market, onChange: onMarketChange },
  domainMarket,
  filterItemsCount,
  onSubmit,
  onFiltersButtonClick,
}: Props) => {
  const { t } = useTranslations(translations);

  const userBannerImg = getImageFileName(domainMarket);

  const getFilterItemsCount = () =>
    filterItemsCount > 0 ? ` (${filterItemsCount})` : '';

  return (
    <Box background="brand" display="flex" paddingX="small">
      <Box className={leftShapesImgStyle}>
        <Box
          component="img"
          src={leftShapesImgUrl}
          alt="Search Banner Left Shapes Img"
          display="block"
          className={shapesImageStyle}
          style={{ float: 'right' }}
        />
      </Box>

      <ContentBlock width="large">
        <Box display="flex" flexDirection="row" width="full">
          <form onSubmit={onSubmit} style={{ width: '100%' }}>
            <Box paddingY={{ mobile: 'large', tablet: 'xxlarge' }}>
              <Stack space="large">
                <Badge tone="positive">{t('EARLY_RELEASE')}</Badge>
                <Heading level="1">
                  {t('FIND_THE_RIGHT_PEOPLE_ON_SEEK')}
                </Heading>
                <Text>
                  {t('SEARCH_FOR_PEOPLE_WHO_HAVE_MADE_THEIR_PROFILES_PUBLIC')}
                </Text>
                <Columns space="small" collapseBelow="tablet">
                  <Column>
                    <TextField
                      id="searchInput"
                      label=""
                      aria-label="search-input"
                      value={searchQuery}
                      icon={<IconSearch />}
                      placeholder={t('SEARCH_NAMES')}
                      tone={valid ? 'neutral' : 'critical'}
                      message={errorMessage}
                      onClear={onSearchQueryClear}
                      onChange={onSearchQueryChange}
                    />
                  </Column>
                  <Column width="content">
                    <Box style={{ minWidth: 190 }}>
                      <MarketSelector
                        marketSelected={market}
                        onMarketChange={onMarketChange}
                      />
                    </Box>
                  </Column>

                  <Column width="content">
                    <Button tone="brandAccent" variant="solid" type="submit">
                      {t('SEEK')}
                    </Button>
                  </Column>
                  <Column width="content">
                    <Hidden above="mobile">
                      <Button
                        icon={<IconFilter />}
                        variant="ghost"
                        onClick={onFiltersButtonClick}
                      >
                        {t('FILTERS')}
                        {getFilterItemsCount()}
                      </Button>
                    </Hidden>
                  </Column>
                </Columns>
              </Stack>
            </Box>
          </form>
          <Box className={userProfilesImgStyle}>
            <Box
              component="img"
              src={userBannerImg}
              alt="Search Banner Img"
              display="block"
              alignItems="flexStart"
              style={{
                height: '412px',
                width: 'auto',
              }}
            />
          </Box>
        </Box>
      </ContentBlock>

      <Box className={rightShapesImgStyle}>
        <Box
          component="img"
          src={rightShapesImgUrl}
          alt="Search Banner Right Shapes Img"
          display="block"
          style={{
            float: 'left',
            height: '412px',
            width: 'auto',
          }}
        />
      </Box>
    </Box>
  );
};
